/**
 * Cloud Agent — lucide icon allowlist (GENUI-3).
 *
 * MIRRORS the server `ICON_ALLOWLIST` in `domains/cloudAgent/layoutSchema.ts`.
 * A block carries an icon NAME string (LLM-chosen, schema-validated); the renderer
 * resolves it to a component here. Anything off-list resolves to `null` so a bad
 * name can never crash the renderer.
 */
import {
  Wallet,
  ShoppingCart,
  Package,
  Percent,
  PiggyBank,
  Receipt,
  TrendingUp,
  TrendingDown,
  BarChart3,
  DollarSign,
  Utensils,
  AlertTriangle,
  CheckCircle,
  Store,
  Tag,
  LucideIcon,
} from 'lucide-react';
import { Tint } from '../../../../../services/admin/cloudAgentTypes';
import {
  iconBgGreen,
  iconBgRed,
  iconBgPurple,
  iconBgBlue,
  iconBgAmber,
} from '../../../../../styling/stylingHelper';

/** Name → component map. Keys mirror the server `ICON_ALLOWLIST` exactly. */
export const ICON_MAP: Record<string, LucideIcon> = {
  Wallet,
  ShoppingCart,
  Package,
  Percent,
  PiggyBank,
  Receipt,
  TrendingUp,
  TrendingDown,
  BarChart3,
  DollarSign,
  Utensils,
  AlertTriangle,
  CheckCircle,
  Store,
  Tag,
};

/** Resolve an icon name to its lucide component, or `null` when off-list/absent. */
export function resolveIcon(name?: string): LucideIcon | null {
  if (!name) return null;
  return ICON_MAP[name] ?? null;
}

/** Solid hex per tint (icon stroke color). Mirrors the Phase-6 card convention. */
const TINT_SOLID: Record<Tint, string> = {
  green: '#079455',
  red: '#D92D20',
  purple: '#6938EF',
  blue: '#2E90FA',
  amber: '#F59E0B',
};

/** Tinted tile background per tint (the `iconBg*` tokens = solid hex + 1A alpha). */
const TINT_BG: Record<Tint, string> = {
  green: iconBgGreen,
  red: iconBgRed,
  purple: iconBgPurple,
  blue: iconBgBlue,
  amber: iconBgAmber,
};

export function tintSolid(tint?: Tint): string {
  return TINT_SOLID[tint ?? 'blue'];
}

export function tintBg(tint?: Tint): string {
  return TINT_BG[tint ?? 'blue'];
}
